import React from 'react';
import ReactMarkdown from 'react-markdown';
import PropTypes from 'prop-types';
import {
	H1,
	H2,
	H3,
	H4,
	H5,
	H6,
	Paragraph,
	Text,
	OrderedList,
	UnorderedList,
	ListItem,
} from 'components/core/Typography';
import { Group } from 'components/core/Page';
import { Link } from 'components/core/Link';
import './MarkdownWrapper.scss';

const markdownComponentMap = {
	h1: ({ node: _node, ...props }) => (
		<H1
			className="ss-type-large"
			{...props}
		/>
	),
	h2: ({ node: _node, ...props }) => (
		<div
			style={{ paddingBottom: 'inherit' }}
			className="card-title"
		>
			<H2
				className="ss-type-large"
				{...props}
			/>
		</div>
	),
	h3: ({ node: _node, ...props }) => (
		<H3
			size="mediumLarge"
			weight="bold"
			{...props}
		/>
	),
	h4: ({ node: _node, ...props }) => (
		<div
			style={{ paddingBottom: 'inherit' }}
			className="card-title"
		>
			<H4
				className="ss-type-medium"
				{...props}
			/>
		</div>
	),
	h5: ({ node: _node, ...props }) => (
		<H5
			className="ss-type-small"
			{...props}
		/>
	),
	h6: ({ node: _node, ...props }) => (
		<div
			style={{ paddingBottom: 'inherit' }}
			className="card-title"
		>
			<H6
				className="ss-type-small"
				{...props}
			/>
		</div>
	),
	p: ({ node: _node, ...props }) => <Paragraph {...props} />,
	ol: ({ node: _node, ...props }) => <OrderedList {...props} />,
	ul: ({ node: _node, ...props }) => <UnorderedList {...props} />,
	li: ({ node: _node, ...props }) => <ListItem {...props} />,
	a: ({ node: _node, ...props }) => (
		<Link
			{...props}
			// tertiary={props.color === 'white'}
		/>
	),
	blockquote: ({ node: _node, ...props }) => (
		<div
			style={{ paddingLeft: '2.31rem' }}
			{...props}
		/>
	),
};

/* eslint-disable react/prop-types */
const InlineP = ({ node: _node, ...props }) => <Text {...props} />;
const InlineBlockquote = ({ node: _node, ...props }) => (
	<span
		style={{ paddingLeft: '2.31rem' }}
		{...props}
	/>
);
const InlineH2 = ({ node: _node, ...props }) => (
	<span className="card-title">
		<H2
			className="ss-type-large"
			{...props}
		/>
	</span>
);
const InlineH4 = ({ node: _node, ...props }) => (
	<span className="card-title">
		<H4
			className="ss-type-medium"
			{...props}
		/>
	</span>
);
const InlineH6 = ({ node: _node, ...props }) => (
	<span className="card-title">
		<H6
			className="ss-type-small"
			{...props}
		/>
	</span>
);
/* eslint-enable react/prop-types */

const inlineMarkdownComponentMap = {
	...markdownComponentMap,
	p: InlineP,
	blockquote: InlineBlockquote,
	h2: InlineH2,
	h4: InlineH4,
	h6: InlineH6,
};

export default function MarkdownWrapper({
	content,
	components,
	componentProps,
	inline,
	...args
}) {
	const inlineComponentMap = inline
		? inlineMarkdownComponentMap
		: markdownComponentMap;

	// Create component map with additional props if provided
	const createComponents = (baseComponents) => {
		if (!componentProps) return baseComponents;

		return Object.keys(baseComponents).reduce((acc, key) => {
			const Component = baseComponents[key];
			acc[key] = (props) => {
				// Merge className from componentProps and props to ensure both are applied
				const mergedClassName = [
					componentProps.className,
					props.className,
				]
					.filter(Boolean)
					.join(' ');
				return (
					<Component
						{...componentProps}
						{...props}
						className={mergedClassName || undefined}
					/>
				);
			};
			return acc;
		}, {});
	};

	const mergedComponents = {
		...createComponents(inlineComponentMap),
		...createComponents(components || {}),
	};

	const Wrapper = inline ? 'span' : 'div';

	return (
		<Wrapper className="markdown-wrapper">
			{content?.map((item) =>
				inline ? (
					<ReactMarkdown
						key={item.value}
						components={mergedComponents}
						{...args}
					>
						{item.value}
					</ReactMarkdown>
				) : (
					<Group key={item.value}>
						<ReactMarkdown
							components={mergedComponents}
							{...args}
						>
							{item.value}
						</ReactMarkdown>
					</Group>
				),
			)}
		</Wrapper>
	);
}

MarkdownWrapper.propTypes = {
	content: PropTypes.arrayOf(PropTypes.object),
	components: PropTypes.objectOf(PropTypes.Component),
	componentProps: PropTypes.object,
	className: PropTypes.string,
	inline: PropTypes.bool,
};
